import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { useHippoText } from '../../../../hooks/useHippoText';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { useRecoilValue } from 'recoil';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import { getCoverableIndex } from '../../../../utils/coverablesUtil';
import { useUpdateCoverableInformation } from '../../../../recoil/hooks/useUpdateCoverableInformation';
import { DateField } from '@flos/react-ui';
import { useSaveCoverableInformation } from '../../../../hooks/useSaveCoverableInformation';
import type { CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { useState } from 'react';
import { convertDateToLocalDateDTO, convertLocalDateDTOToDate } from '../../../../utils/dateUtil';
import { isRequestPendingAtom } from '../../../../recoil/atoms/isRequestPendingAtom';
import { track } from '../../../../utils/trackingUtil';

const LeasingPeriodStep = ({
    goNext,
    goPrevious
}: {
    goNext: () => void;
    goPrevious: () => void;
}): React.JSX.Element => {
    const isRequestPending = useRecoilValue(isRequestPendingAtom);
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const getHippoText = useHippoText();
    const { cars = [] } = coverables;
    const coverableIndex = getCoverableIndex(cars, currentProduct.fixedId);
    const coverable = cars[coverableIndex];
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [startDate, setStartDate] = useState(
        coverable.leasingStartDate !== undefined
            ? convertLocalDateDTOToDate(coverable.leasingStartDate)
            : undefined
    );
    const [endDate, setEndDate] = useState(
        coverable.leasingEndDate !== undefined
            ? convertLocalDateDTOToDate(coverable.leasingEndDate)
            : undefined
    );

    const onNext = (): void => {
        if (!startDate || !endDate) return;
        setIsLoading(true);
        const localStartDate = convertDateToLocalDateDTO(startDate);
        const localEndDate = convertDateToLocalDateDTO(endDate);

        const updatedCoverable: CoverableInformationToUpdateDTO = {
            car: {
                publicId: coverable.publicId,
                fixedId: coverable.fixedId,
                leasingStartDate: localStartDate,
                leasingEndDate: localEndDate
            },
            productToUpdate: currentProduct.flavourName
        };

        updateCoverableInformation({
            leasingStartDate: localStartDate,
            leasingEndDate: localEndDate
        });

        void saveCoverableInformation(updatedCoverable).then(() => {
            track(`${currentProduct.flavourName}ContentCoverableInformationLeasingPeriod`, [
                { leasingStartDate: localStartDate },
                { leasingEndDate: localEndDate }
            ]);
            setIsLoading(false);
            goNext();
        });
    };

    return (
        <WizardPagetemplate
            onNext={onNext}
            onPrevious={goPrevious}
            disableNext={
                isLoading ||
                !startDate ||
                !endDate ||
                isRequestPending ||
                startDate.getTime() >= endDate.getTime()
            }
            disablePrevious={isRequestPending || isLoading}
            isNextLoading={isLoading || isRequestPending}
        >
            <CoverableDetailsTemplate
                header={getHippoText('DSS-COVERABLE-INFORMATION-STEP-LEASING-PERIOD-HEADER')}
                subheader={getHippoText('DSS-COVERABLE-INFORMATION-STEP-LEASING-PERIOD-TEXT')}
            >
                <DateField
                    label={getHippoText('DSS-COVERABLE-INFORMATION-STEP-LEASING-START-DATE')}
                    onChange={(date: Date): void => {
                        setStartDate(date);
                    }}
                    selected={startDate}
                    disabled={isLoading || isRequestPending}
                />
                <DateField
                    label={getHippoText('DSS-COVERABLE-INFORMATION-STEP-LEASING-END-DATE')}
                    onChange={(date: Date): void => {
                        setEndDate(date);
                    }}
                    minDate={
                        startDate && startDate.getTime() > new Date().getTime()
                            ? new Date(startDate).setDate(startDate.getDate() + 1)
                            : new Date()
                    }
                    isValid={
                        startDate && endDate && startDate.getTime() >= endDate.getTime()
                            ? false
                            : undefined
                    }
                    selected={endDate}
                    errorText={getHippoText(
                        'DSS-COVERABLE-INFORMATION-STEP-LEASING-END-DATE-ERROR-MESSAGE'
                    )}
                    disabled={isLoading || isRequestPending}
                />
            </CoverableDetailsTemplate>
        </WizardPagetemplate>
    );
};

export default LeasingPeriodStep;
